import { useState, useMemo, useEffect } from "react";
import { DefaultViewControl } from "@/components/DefaultViewControl";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/AppLayout";
import { Field } from "@/pages/Companies";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Plus, Search, Trash2, Mail, Phone, ShieldCheck, Send, Link2, RefreshCw, FileText,
  Loader2, AlertTriangle, Undo2, Paperclip, Eye, Landmark, ClipboardList, Clock,
} from "lucide-react";

export const SUPPLIER_SECTIONS = [
  { key: "company", label: "Company, ABN & key contacts", always: true },
  { key: "banking", label: "Bank account details (BSB / account)" },
  { key: "insurance_pl", label: "Public liability insurance — certificate + expiry", doc: true },
  { key: "insurance_wc", label: "Workers compensation insurance", doc: true },
  { key: "licences", label: "Contractor licence(s)", doc: true },
  { key: "quality_cert", label: "Quality certification (if held)", doc: true },
  { key: "whs", label: "WHS management questionnaire" },
  { key: "capability", label: "Trade capability, lead times & references" },
  { key: "agreement", label: "Signed contractor agreement", doc: true },
];
const KIND_LABEL = {
  insurance_pl: "Public liability", insurance_wc: "Workers comp", licences: "Licence",
  quality_cert: "Quality cert", agreement: "Signed agreement",
};
const EVAL_CRITERIA = [
  ["quality", "Quality systems & certification"],
  ["insurance", "Insurances current & adequate"],
  ["capability", "Capability, capacity & lead times"],
  ["whs", "WHS compliance"],
  ["commercial", "Commercial terms acceptable"],
];
const PRIMARY_TABS = [["materials", "Materials"], ["contractors_labour", "Contractors / Labour"], ["other", "Other"]];
const APPROVED = ["approved", "approved_legacy"];
const STATUS_CHIP = {
  approved: ["Approved", "border-emerald-300 bg-emerald-50 text-emerald-700"],
  approved_legacy: ["Legacy — review required", "border-amber-300 bg-amber-50 text-amber-800"],
  onboarding_sent: ["Onboarding sent", "border-cyan-300 bg-cyan-50 text-cyan-700"],
  pending_review: ["Awaiting review", "border-violet-300 bg-violet-50 text-violet-700"],
  changes_requested: ["Changes requested", "border-rose-300 bg-rose-50 text-rose-700"],
};
const EMPTY = { name: "", contact_name: "", email: "", phone: "", address: "", primary_category: "materials", subcategories: [], critical: false, notes: "" };

const fmtD = (s) => (s ? new Date(s).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" }) : "—");
const daysAgo = (iso) => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
const obLink = (s) => (s?.onboarding?.token ? `${window.location.origin}/supplier-onboarding/${s.onboarding.token}` : null);
const copyText = (txt) => navigator.clipboard.writeText(txt).then(() => toast.success("Link copied")).catch(() => toast.error("Copy failed — copy it manually"));
const activeDocs = (s) => (s.documents || []).filter((d) => !d.superseded);

export const StatusChip = ({ s }) => {
  const [label, cls] = STATUS_CHIP[s.approval_status] || ["Not onboarded", "border-slate-300 bg-slate-100 text-slate-500"];
  const temp = s.temp_approval && !["approved", "approved_legacy"].includes(s.approval_status);
  return (
    <>
      <Badge variant="outline" className={`rounded-full text-[9.5px] font-bold ${cls}`} data-testid={`supplier-status-${s.id}`}>
        {s.approval_status === "approved" && <ShieldCheck size={9} className="mr-1" />}{label}
        {s.approval_status === "onboarding_sent" && s.onboarding?.sent_at ? ` · ${daysAgo(s.onboarding.sent_at)}d ago` : ""}
      </Badge>
      {temp && (
        <Badge variant="outline" className="rounded-full text-[9.5px] font-bold border-amber-300 bg-amber-50 text-amber-800" data-testid={`supplier-temp-chip-${s.id}`}>
          <Clock size={9} className="mr-1" />Temp approved
        </Badge>
      )}
    </>
  );
};

const DocsChip = ({ s }) => {
  const docs = activeDocs(s);
  const today = new Date().toISOString().slice(0, 10);
  const withExp = docs.filter((d) => d.expiry);
  const expired = withExp.some((d) => d.expiry < today);
  const soon = withExp.filter((d) => d.expiry >= today).sort((a, b) => (a.expiry < b.expiry ? -1 : 1))[0];
  const soonDays = soon ? Math.round((new Date(soon.expiry) - new Date(today)) / 86400000) : null;
  if (expired) return <Badge variant="outline" className="rounded-full text-[9.5px] font-bold border-rose-300 bg-rose-50 text-rose-700"><AlertTriangle size={9} className="mr-1" />Docs expired</Badge>;
  if (soon && soonDays <= 30) return <Badge variant="outline" className="rounded-full text-[9.5px] font-bold border-amber-300 bg-amber-50 text-amber-800"><AlertTriangle size={9} className="mr-1" />Expires {fmtD(soon.expiry)}</Badge>;
  if (docs.length) return <Badge variant="outline" className="rounded-full text-[9.5px] font-bold border-emerald-300 bg-emerald-50 text-emerald-700">All current</Badge>;
  if (s.onboarding?.status === "sent") return <Badge variant="outline" className="rounded-full text-[9.5px] font-bold border-slate-300 bg-slate-100 text-slate-500">Awaiting submission</Badge>;
  return <span className="text-slate-300 text-xs">—</span>;
};

const CriticalChip = () => (
  <Badge variant="outline" className="rounded-full text-[9px] font-bold border-rose-300 bg-rose-50 text-rose-700">CRITICAL</Badge>
);

function SubcatPicker({ subcats, value = [], onChange }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {subcats.map((sc) => {
        const on = value.includes(sc.slug);
        return (
          <button key={sc.slug} type="button" data-testid={`subcat-pick-${sc.slug}`}
            onClick={() => onChange(on ? value.filter((v) => v !== sc.slug) : [...value, sc.slug])}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border transition-colors ${on ? "bg-cyan-600 border-cyan-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-cyan-400"}`}>
            {sc.label}
          </button>
        );
      })}
    </div>
  );
}

function SupplierForm({ draft, setDraft, subcats }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Supplier name" required><Input data-testid="supplier-name-input" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} /></Field>
        <Field label="Primary contact"><Input data-testid="supplier-contact-input" value={draft.contact_name || ""} onChange={(e) => setDraft({ ...draft, contact_name: e.target.value })} /></Field>
        <Field label="Email"><Input data-testid="supplier-email-input" value={draft.email || ""} onChange={(e) => setDraft({ ...draft, email: e.target.value })} /></Field>
        <Field label="Phone"><Input data-testid="supplier-phone-input" value={draft.phone || ""} onChange={(e) => setDraft({ ...draft, phone: e.target.value })} /></Field>
      </div>
      <Field label="Address"><Input data-testid="supplier-address-input" value={draft.address || ""} onChange={(e) => setDraft({ ...draft, address: e.target.value })} /></Field>
      <Field label="Primary category" required>
        <Select value={draft.primary_category || "materials"} onValueChange={(v) => setDraft({ ...draft, primary_category: v })}>
          <SelectTrigger data-testid="supplier-primary-category-select"><SelectValue /></SelectTrigger>
          <SelectContent>{PRIMARY_TABS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </Field>
      {(draft.primary_category || "materials") === "materials" && (
        <Field label="Material subcategories">
          <SubcatPicker subcats={subcats} value={draft.subcategories || []} onChange={(v) => setDraft({ ...draft, subcategories: v })} />
        </Field>
      )}
      <label className="flex items-start gap-2 border border-rose-200 bg-rose-50/40 rounded-sm p-2.5 cursor-pointer">
        <Checkbox data-testid="supplier-critical-checkbox" checked={!!draft.critical} onCheckedChange={(v) => setDraft({ ...draft, critical: !!v })} className="mt-0.5" />
        <span className="text-xs text-slate-700"><b>Critical supplier</b> — essential to delivery; stricter document requirements &amp; tighter expiry alerts</span>
      </label>
      <Field label="Notes"><Textarea data-testid="supplier-notes-input" rows={2} value={draft.notes || ""} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></Field>
    </div>
  );
}

function SendOnboardingDialog({ sup, open, onClose }) {
  const qc = useQueryClient();
  const isContractor = sup?.primary_category === "contractors_labour";
  const [checked, setChecked] = useState({});
  const [message, setMessage] = useState("");
  const [critical, setCritical] = useState(false);
  const [result, setResult] = useState(null);
  const agreement = sup?.agreement_template || sup?.onboarding?.agreement;

  useEffect(() => {
    if (open && sup) {
      const def = isContractor
        ? ["banking", "insurance_pl", "insurance_wc", "licences", "agreement"]
        : ["banking", "insurance_pl", "quality_cert"];
      setChecked(Object.fromEntries(def.map((k) => [k, true])));
      setMessage(""); setResult(null); setCritical(!!sup.critical);
    }
  }, [open, sup?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const uploadAgreement = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData(); fd.append("file", file);
      return (await api.post(`/suppliers/${sup.id}/onboarding/agreement`, fd)).data;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["suppliers"] }); toast.success("Agreement attached"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Upload failed"),
  });

  const send = useMutation({
    mutationFn: async () => (await api.post(`/suppliers/${sup.id}/onboarding/send`, {
      sections: SUPPLIER_SECTIONS.filter((s) => !s.always).map((s) => ({ key: s.key, required: !!checked[s.key] })),
      message: message.trim() || null,
      critical,
    })).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      setResult(d);
      if (d.email_status === "sent" || d.email_status === "scheduled") toast.success("Onboarding link emailed to the supplier");
      else toast.warning("Email could not be delivered — copy the link below and send it yourself");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not send the onboarding link"),
  });

  if (!sup) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="send-onboarding-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2"><ShieldCheck size={18} className="text-cyan-600" /> Onboard {sup.name}</DialogTitle>
        </DialogHeader>
        {result ? (
          <div className="space-y-3" data-testid="onboarding-sent-panel">
            <div className="border border-emerald-200 bg-emerald-50 rounded-sm p-3 text-sm text-emerald-800">
              <b>Onboarding link created</b> — valid until {fmtD(result.expires_at)}.{" "}
              {result.email_status === "sent" || result.email_status === "scheduled"
                ? `Emailed to ${sup.email}.`
                : "The email could not be delivered — copy the link and send it to the supplier yourself."}
            </div>
            <div className="flex items-center gap-2">
              <Input readOnly value={result.link} className="h-9 rounded-sm font-mono text-[11px]" data-testid="onboarding-link-input" />
              <Button size="sm" onClick={() => copyText(result.link)} className="rounded-sm h-9 bg-cyan-600 hover:bg-cyan-700 shrink-0" data-testid="copy-onboarding-link-btn"><Link2 size={13} className="mr-1.5" />Copy</Button>
            </div>
            <p className="text-[11px] text-slate-500">The link isn't tied to one email — the supplier can forward it to the right person in their organisation. Progress saves as they go; it locks once submitted.</p>
            <DialogFooter><Button onClick={onClose} className="rounded-sm bg-slate-900 hover:bg-slate-800" data-testid="onboarding-done-btn">Done</Button></DialogFooter>
          </div>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">A secure link emailed to <b>{sup.email || "— add an email first"}</b> · valid 14 days · can be forwarded within their organisation.</p>
            {sup.approval_status === "pending_review" && (
              <div className="border border-amber-200 bg-amber-50 rounded-sm px-3 py-2 text-[11px] text-amber-800">
                This supplier has a submission awaiting review — sending a new request replaces it and re-opens their form.
              </div>
            )}
            <Field label="Supplier criticality">
              <div className="flex gap-2">
                <button type="button" data-testid="criticality-critical" onClick={() => setCritical(true)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-bold border ${critical ? "bg-rose-600 border-rose-600 text-white" : "bg-white border-slate-300 text-slate-600"}`}>Critical</button>
                <button type="button" data-testid="criticality-noncritical" onClick={() => setCritical(false)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-bold border ${!critical ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-300 text-slate-600"}`}>Non-critical</button>
              </div>
            </Field>
            <div>
              <div className="stamp-label text-slate-600 mb-1.5">Selection criteria — ticked sections appear on the supplier's form and are mandatory; unticked sections are left out entirely</div>
              <div className="space-y-1">
                {SUPPLIER_SECTIONS.map((s) => (
                  <label key={s.key} className={`flex items-start gap-2 border rounded-sm px-2.5 py-2 text-xs ${s.always ? "border-slate-200 bg-slate-50 text-slate-500" : "border-slate-200 cursor-pointer hover:bg-slate-50 text-slate-700"}`}>
                    <Checkbox checked={s.always || !!checked[s.key]} disabled={s.always}
                      onCheckedChange={(v) => setChecked((c) => ({ ...c, [s.key]: !!v }))}
                      data-testid={`section-check-${s.key}`} className="mt-0.5" />
                    <span className="flex-1">{s.label}</span>
                    {s.always ? <span className="text-[8.5px] font-extrabold tracking-widest text-slate-400">ALWAYS</span>
                      : checked[s.key] && <span className="text-[8.5px] font-extrabold tracking-widest text-rose-600">MANDATORY</span>}
                  </label>
                ))}
              </div>
            </div>
            {checked.agreement && (
              <div className="border border-slate-200 rounded-sm p-2.5">
                <div className="stamp-label text-slate-600 mb-1.5">Contractor agreement to be signed</div>
                {agreement ? (
                  <div className="flex items-center justify-between text-xs bg-slate-50 border border-slate-200 rounded-sm px-2.5 py-1.5">
                    <span className="flex items-center gap-1.5 truncate"><Paperclip size={11} className="text-slate-400" />{agreement.name}</span>
                    <span className="text-emerald-600 font-bold text-[10px]">Attached</span>
                  </div>
                ) : (
                  <label className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-sm p-3 text-xs text-slate-500 cursor-pointer hover:border-cyan-400" data-testid="agreement-upload-zone">
                    {uploadAgreement.isPending ? <Loader2 size={13} className="animate-spin" /> : <Paperclip size={13} />}
                    Attach the agreement PDF the supplier must sign &amp; return
                    <input type="file" className="hidden" accept=".pdf,.doc,.docx" onChange={(e) => e.target.files?.[0] && uploadAgreement.mutate(e.target.files[0])} />
                  </label>
                )}
              </div>
            )}
            <Field label="Message to the supplier (optional)">
              <Textarea data-testid="onboarding-message-input" rows={2} value={message} onChange={(e) => setMessage(e.target.value)} maxLength={2000}
                placeholder="Hi team — please complete our supplier onboarding so we can keep ordering from you." />
            </Field>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
              <Button data-testid="send-onboarding-btn" disabled={send.isPending || !sup.email || (checked.agreement && !agreement)}
                title={!sup.email ? "Add an email address to the supplier first" : (checked.agreement && !agreement) ? "Attach the contractor agreement first" : ""}
                onClick={() => send.mutate()} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">
                {send.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Send size={14} className="mr-1.5" />} Send onboarding link
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ProfileDialog({ sup, onClose, subcats, canApprove, canBank, onSendOnboarding }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState(sup);
  const [criteria, setCriteria] = useState(Object.fromEntries(EVAL_CRITERIA.map(([k]) => [k, true])));
  const [evalNote, setEvalNote] = useState("");
  const [rcOpen, setRcOpen] = useState(false);
  const [rcComment, setRcComment] = useState("");
  const [tempOpen, setTempOpen] = useState(false);
  const [tempComment, setTempComment] = useState("");
  useEffect(() => { setDraft(sup); }, [sup?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: async () => (await api.patch(`/suppliers/${sup.id}`, {
      name: draft.name, contact_name: draft.contact_name, email: draft.email, phone: draft.phone,
      address: draft.address, primary_category: draft.primary_category,
      subcategories: draft.subcategories || [], critical: !!draft.critical, notes: draft.notes,
    })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["suppliers"] }); toast.success("Supplier saved"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Save failed"),
  });
  const del = useMutation({
    mutationFn: async () => (await api.delete(`/suppliers/${sup.id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["suppliers"] }); toast.success("Supplier deleted"); onClose(); },
  });
  const resend = useMutation({
    mutationFn: async () => (await api.post(`/suppliers/${sup.id}/onboarding/resend`)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      copyText(d.link);
      toast.success(d.email_status === "sent" ? "Fresh link emailed & copied" : "Fresh link copied — email delivery failed, send it manually");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not resend"),
  });
  const approve = useMutation({
    mutationFn: async () => (await api.post(`/suppliers/${sup.id}/approve`, { criteria, note: evalNote.trim() || null })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["suppliers"] }); qc.invalidateQueries({ queryKey: ["approvals-pending"] }); toast.success(`${sup.name} approved — review due in 12 months`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Approve failed"),
  });
  const requestChanges = useMutation({
    mutationFn: async () => (await api.post(`/suppliers/${sup.id}/request-changes`, { comment: rcComment.trim() })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["suppliers"] }); qc.invalidateQueries({ queryKey: ["approvals-pending"] }); setRcOpen(false); setRcComment(""); toast.success("Changes requested — the supplier's link has been re-opened"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Request failed"),
  });
  const tempApprove = useMutation({
    mutationFn: async () => (await api.post(`/suppliers/${sup.id}/temp-approve`, { comment: tempComment.trim() })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["suppliers"] }); setTempOpen(false); setTempComment(""); toast.success(`${sup.name} temporarily approved — full review still required`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Temp approval failed"),
  });
  const revokeTemp = useMutation({
    mutationFn: async () => (await api.post(`/suppliers/${sup.id}/temp-approve/revoke`, {})).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["suppliers"] }); toast.success("Temporary approval revoked"); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Revoke failed"),
  });

  const viewDoc = async (docId) => {
    try {
      const r = await api.get(`/suppliers/${sup.id}/docs/${docId}`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Could not open the document"); }
  };

  if (!sup || !draft) return null;
  const docs = activeDocs(sup);
  const link = obLink(sup);
  const allPass = EVAL_CRITERIA.every(([k]) => criteria[k]);
  const canApproveNow = canApprove && ["pending_review", "approved_legacy", "changes_requested"].includes(sup.approval_status);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="rounded-sm sm:max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="supplier-profile-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl flex items-center gap-2 flex-wrap">
            {sup.name}
            <StatusChip s={sup} />
            {sup.critical && <CriticalChip />}
          </DialogTitle>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-5">
          <div>
            <div className="stamp-label text-slate-500 mb-2">Details</div>
            <SupplierForm draft={draft} setDraft={setDraft} subcats={subcats} />
            <div className="flex items-center justify-between mt-3">
              <Button size="sm" variant="ghost" className="rounded-sm text-rose-600 h-8" data-testid="delete-supplier-btn"
                onClick={() => window.confirm(`Delete ${sup.name}? This can't be undone.`) && del.mutate()}>
                <Trash2 size={13} className="mr-1.5" /> Delete
              </Button>
              <Button size="sm" data-testid="save-supplier-btn" disabled={save.isPending || !draft.name?.trim()} onClick={() => save.mutate()} className="rounded-sm bg-cyan-600 hover:bg-cyan-700 h-8">
                {save.isPending ? <Loader2 size={13} className="animate-spin mr-1.5" /> : null} Save details
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <div className="stamp-label text-slate-500 mb-2 flex items-center gap-1.5"><ClipboardList size={11} /> Supplier onboarding</div>
              {sup.onboarding ? (
                <div className="border border-slate-200 rounded-sm p-3 space-y-2 text-xs text-slate-600">
                  <div>
                    {sup.onboarding.renewal ? "Document renewal" : "Onboarding"} sent {fmtD(sup.onboarding.sent_at)} by <b>{sup.onboarding.sent_by_name || sup.onboarding.sent_by}</b>
                    {sup.onboarding.status === "sent" && <> · link valid until <b>{fmtD(sup.onboarding.expires_at)}</b></>}
                    {sup.onboarding.status === "submitted" && <> · <b className="text-violet-700">submitted {fmtD(sup.onboarding.submitted_at)}</b></>}
                  </div>
                  {sup.onboarding.changes_note && (
                    <div className="bg-rose-50 border border-rose-200 rounded-sm px-2.5 py-1.5 text-rose-700 italic">Changes requested: “{sup.onboarding.changes_note}”</div>
                  )}
                  <div className="flex flex-wrap gap-1.5">
                    {link && sup.onboarding.status === "sent" && (
                      <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" onClick={() => copyText(link)} data-testid="profile-copy-link-btn">
                        <Link2 size={11} className="mr-1" /> Copy link
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" disabled={resend.isPending} onClick={() => resend.mutate()} data-testid="profile-resend-btn">
                      {resend.isPending ? <Loader2 size={11} className="animate-spin mr-1" /> : <RefreshCw size={11} className="mr-1" />} Resend fresh link
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px]" onClick={onSendOnboarding} data-testid="profile-new-onboarding-btn">
                      <Send size={11} className="mr-1" /> New request
                    </Button>
                  </div>
                </div>
              ) : (
                <Button size="sm" onClick={onSendOnboarding} className="rounded-sm bg-cyan-600 hover:bg-cyan-700 h-8" data-testid="profile-send-onboarding-btn">
                  <Send size={13} className="mr-1.5" /> Send onboarding link
                </Button>
              )}
            </div>

            <div>
              <div className="stamp-label text-slate-500 mb-2 flex items-center gap-1.5"><FileText size={11} /> Documents ({docs.length})</div>
              {docs.length === 0 && <div className="text-xs text-slate-400 italic">No documents on file yet.</div>}
              <div className="space-y-1">
                {docs.map((d) => {
                  const today = new Date().toISOString().slice(0, 10);
                  const expired = d.expiry && d.expiry < today;
                  return (
                    <div key={d.id} className="flex items-center justify-between border border-slate-200 bg-slate-50 rounded-sm px-2.5 py-1.5 text-xs" data-testid={`supplier-doc-${d.id}`}>
                      <span className="truncate flex-1">
                        <b>{KIND_LABEL[d.kind] || d.kind}</b> — {d.name}{d.insurer ? ` · ${d.insurer}` : ""}
                      </span>
                      {d.expiry && (
                        <Badge variant="outline" className={`rounded-full text-[9px] font-bold mx-2 shrink-0 ${expired ? "border-rose-300 bg-rose-50 text-rose-700" : "border-emerald-300 bg-emerald-50 text-emerald-700"}`}>
                          {expired ? "expired " : "exp "}{fmtD(d.expiry)}
                        </Badge>
                      )}
                      <Button size="sm" variant="ghost" className="h-6 w-6 p-0 shrink-0" onClick={() => viewDoc(d.id)} data-testid={`view-doc-${d.id}`}><Eye size={12} /></Button>
                    </div>
                  );
                })}
              </div>
            </div>

            <div>
              <div className="stamp-label text-slate-500 mb-2 flex items-center gap-1.5"><Landmark size={11} /> Bank details</div>
              {sup.bank?.on_file && !sup.bank.bsb ? (
                <div className="text-xs text-slate-500 border border-slate-200 bg-slate-50 rounded-sm px-2.5 py-2" data-testid="bank-on-file">On file — visible to admins &amp; supplier approvers only.</div>
              ) : sup.bank?.bsb ? (
                <div className="text-xs text-slate-700 border border-slate-200 bg-slate-50 rounded-sm px-2.5 py-2 font-mono" data-testid="bank-details">
                  {sup.bank.account_name && <div>{sup.bank.account_name}</div>}
                  <div>BSB {sup.bank.bsb} · Acct {sup.bank.account_number}</div>
                </div>
              ) : (
                <div className="text-xs text-slate-400 italic">Not provided.</div>
              )}
            </div>

            {(sup.abn || (sup.contacts || []).length > 0 || sup.whs_answers || sup.capability) && (
              <div data-testid="submitted-details">
                <div className="stamp-label text-slate-500 mb-2">Submitted details</div>
                <div className="border border-slate-200 rounded-sm p-3 space-y-2 text-xs text-slate-700">
                  {(sup.abn || sup.website) && (
                    <div>{sup.abn && <>ABN <b className="font-mono">{sup.abn}</b></>}{sup.abn && sup.website ? " · " : ""}{sup.website}</div>
                  )}
                  {(sup.contacts || []).filter((c) => c?.name).map((c, i) => (
                    <div key={i} className="text-slate-600">
                      <b className="text-slate-800">{c.name}</b>{c.role ? ` — ${c.role}` : ""}{c.email ? ` · ${c.email}` : ""}{c.phone ? ` · ${c.phone}` : ""}
                    </div>
                  ))}
                  {sup.whs_answers && (
                    <div>
                      <span className="font-semibold text-slate-800">WHS:</span>{" "}
                      {[["policy", "Written policy"], ["swms", "SWMS"], ["incidents", "Incident procedure"], ["training", "Trained & licensed"]]
                        .filter(([k]) => sup.whs_answers[k])
                        .map(([k, l]) => `${l} ${sup.whs_answers[k] === "yes" ? "✓" : "✗"}`).join(" · ")}
                      {sup.whs_answers.notes && <div className="italic text-slate-500 mt-0.5">“{sup.whs_answers.notes}”</div>}
                    </div>
                  )}
                  {sup.capability?.products && <div><span className="font-semibold text-slate-800">Supplies:</span> {sup.capability.products}</div>}
                  {sup.capability?.lead_times && <div><span className="font-semibold text-slate-800">Lead times:</span> {sup.capability.lead_times}</div>}
                  {sup.capability?.references && <div><span className="font-semibold text-slate-800">References:</span> {sup.capability.references}</div>}
                  {sup.declaration?.name && <div className="text-[10.5px] text-slate-400">Declared true &amp; correct by {sup.declaration.name} · {fmtD(sup.declaration.at)}</div>}
                </div>
              </div>
            )}

            {canApprove && ["onboarding_sent", "pending_review", "changes_requested"].includes(sup.approval_status) && (
              <div className="border border-amber-200 bg-amber-50/50 rounded-sm p-3" data-testid="supplier-temp-panel">
                {sup.temp_approval ? (
                  <>
                    <div className="stamp-label text-amber-700 mb-1 flex items-center gap-1.5"><Clock size={12} /> Temporary approval active</div>
                    <p className="text-[11px] text-slate-600">
                      Granted by <b>{sup.temp_approval.by_name || sup.temp_approval.by}</b> · {fmtD(sup.temp_approval.at)}
                      {sup.temp_approval.note && <span className="italic"> — “{sup.temp_approval.note}”</span>}
                    </p>
                    <p className="text-[10px] text-amber-700 mt-1">This supplier can be used on orders and crews while the full review is completed.</p>
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-[11px] mt-2 border-amber-300 text-amber-800 hover:bg-amber-100"
                      disabled={revokeTemp.isPending} onClick={() => revokeTemp.mutate()} data-testid="supplier-temp-revoke-btn">
                      {revokeTemp.isPending ? <Loader2 size={12} className="animate-spin mr-1" /> : null} Revoke temporary approval
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="stamp-label text-amber-700 mb-1 flex items-center gap-1.5"><Clock size={12} /> Temporary approval override</div>
                    <p className="text-[11px] text-slate-600">Need this supplier before the review is finished? Grant a temporary approval with a note — it's logged on the approval trail and full approval is still required.</p>
                    <Button size="sm" className="rounded-sm h-7 text-[11px] mt-2 bg-amber-600 hover:bg-amber-700 text-white"
                      onClick={() => setTempOpen(true)} data-testid="supplier-temp-approve-btn">
                      <Clock size={12} className="mr-1" /> Temp approve…
                    </Button>
                  </>
                )}
              </div>
            )}

            {canApproveNow && (
              <div className="border border-violet-200 bg-violet-50/40 rounded-sm p-3" data-testid="supplier-eval-panel">
                <div className="stamp-label text-violet-700 mb-2">Evaluation — supplier approval criteria</div>
                {EVAL_CRITERIA.map(([k, label]) => (
                  <div key={k} className="flex items-center justify-between py-1 border-b border-violet-100 last:border-b-0 text-xs text-slate-700">
                    <span>{label}</span>
                    <div className="flex gap-1">
                      <button type="button" data-testid={`eval-pass-${k}`} onClick={() => setCriteria((c) => ({ ...c, [k]: true }))}
                        className={`px-2.5 py-0.5 rounded-sm text-[10px] font-bold border ${criteria[k] ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-slate-300 text-slate-500"}`}>Pass</button>
                      <button type="button" data-testid={`eval-fail-${k}`} onClick={() => setCriteria((c) => ({ ...c, [k]: false }))}
                        className={`px-2.5 py-0.5 rounded-sm text-[10px] font-bold border ${!criteria[k] ? "bg-rose-600 border-rose-600 text-white" : "bg-white border-slate-300 text-slate-500"}`}>Fail</button>
                    </div>
                  </div>
                ))}
                <Textarea rows={2} value={evalNote} onChange={(e) => setEvalNote(e.target.value)} maxLength={2000}
                  className="rounded-sm text-xs mt-2" placeholder="Evaluation note (optional)" data-testid="eval-note-input" />
                <div className="flex gap-1.5 mt-2 justify-end flex-wrap">
                  {sup.approval_status === "pending_review" && (
                    <Button size="sm" className="rounded-sm h-8 text-[11px] bg-rose-600 hover:bg-rose-700 text-white" onClick={() => setRcOpen(true)} data-testid="supplier-request-changes-btn">
                      <Undo2 size={12} className="mr-1" /> Request changes
                    </Button>
                  )}
                  <Button size="sm" className="rounded-sm h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                    disabled={approve.isPending || !allPass}
                    title={allPass ? "Approve this supplier" : "Set every criterion to Pass, or request changes"}
                    onClick={() => approve.mutate()} data-testid="supplier-approve-btn">
                    {approve.isPending ? <Loader2 size={12} className="animate-spin mr-1" /> : <ShieldCheck size={12} className="mr-1" />} Approve supplier
                  </Button>
                </div>
                {!allPass && <p className="text-[10px] text-rose-600 mt-1 text-right">A failed criterion blocks approval — request changes instead.</p>}
              </div>
            )}

            {sup.approval_status === "approved" && sup.review_due && (
              <div className="text-[11px] text-slate-500">Annual re-evaluation due <b>{fmtD(sup.review_due)}</b>{sup.evaluation?.by_name ? ` · last approved by ${sup.evaluation.by_name}` : ""}</div>
            )}

            {(sup.approval_trail || []).length > 0 && (
              <div>
                <div className="stamp-label text-slate-500 mb-1.5">Approval trail</div>
                <div className="space-y-1 max-h-36 overflow-y-auto pr-1">
                  {[...sup.approval_trail].reverse().map((ev, i) => (
                    <div key={i} className="text-[11px] text-slate-600">
                      <b className="capitalize">{(ev.event || "").replace(/_/g, " ")}</b> · {ev.by_name || ev.by} · {fmtD(ev.at)}
                      {ev.note && <span className="italic text-slate-500"> — “{ev.note}”</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <Dialog open={tempOpen} onOpenChange={setTempOpen}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="supplier-temp-dialog">
            <DialogHeader><DialogTitle className="font-display text-lg flex items-center gap-2"><Clock size={16} className="text-amber-600" /> Temporary approval — {sup.name}</DialogTitle></DialogHeader>
            <p className="text-xs text-slate-500">The supplier becomes usable on orders and crews immediately, marked <b>Temp approved</b>. The full review stays open and your note is logged on the approval trail.</p>
            <Textarea rows={4} value={tempComment} onChange={(e) => setTempComment(e.target.value)} maxLength={2000}
              className="rounded-sm text-sm" placeholder="Why is a temporary approval needed? (required — e.g. urgent order, paperwork in progress)"
              data-testid="supplier-temp-comment" />
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setTempOpen(false)} className="rounded-sm">Cancel</Button>
              <Button disabled={tempApprove.isPending || tempComment.trim().length < 3} onClick={() => tempApprove.mutate()}
                className="rounded-sm bg-amber-600 hover:bg-amber-700 text-white" data-testid="supplier-temp-confirm">
                {tempApprove.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Clock size={14} className="mr-1.5" />} Grant temporary approval
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={rcOpen} onOpenChange={setRcOpen}>
          <DialogContent className="rounded-sm sm:max-w-md" data-testid="supplier-rc-dialog">
            <DialogHeader><DialogTitle className="font-display text-lg flex items-center gap-2"><Undo2 size={16} className="text-rose-600" /> Request changes — {sup.name}</DialogTitle></DialogHeader>
            <Textarea rows={4} value={rcComment} onChange={(e) => setRcComment(e.target.value)} maxLength={2000}
              className="rounded-sm text-sm" placeholder="Tell the supplier what's missing or needs fixing — their link re-opens for 14 days with your note…"
              data-testid="supplier-rc-comment" />
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setRcOpen(false)} className="rounded-sm">Cancel</Button>
              <Button disabled={requestChanges.isPending || rcComment.trim().length < 3} onClick={() => requestChanges.mutate()}
                className="rounded-sm bg-rose-600 hover:bg-rose-700 text-white" data-testid="supplier-rc-send">
                {requestChanges.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Undo2 size={14} className="mr-1.5" />} Send back to supplier
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
}

export default function Suppliers() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [tab, setTab] = useState("materials");
  const [subcat, setSubcat] = useState("all");
  const [search, setSearch] = useState("");
  const [profileId, setProfileId] = useState(null);
  const [sendFor, setSendFor] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createDraft, setCreateDraft] = useState({ ...EMPTY });
  const [addingSub, setAddingSub] = useState(false);
  const [newSubLabel, setNewSubLabel] = useState("");

  const canApprove = !!(user?.supplier_approver || user?.role === "super_admin");
  const canBank = !!(user?.supplier_approver || ["admin", "super_admin"].includes(user?.role));

  const { data: suppliers = [] } = useQuery({
    queryKey: ["suppliers", search],
    queryFn: async () => (await api.get("/suppliers", { params: search ? { search } : {} })).data,
  });
  const { data: subcats = [] } = useQuery({
    queryKey: ["supplier-subcats"], queryFn: async () => (await api.get("/supplier-subcats")).data,
  });
  const subLabel = useMemo(() => Object.fromEntries(subcats.map((s) => [s.slug, s.label])), [subcats]);

  useEffect(() => {
    const openId = params.get("open");
    if (openId && suppliers.length && !profileId) {
      const s = suppliers.find((x) => x.id === openId);
      if (s) { setTab(s.primary_category || "other"); setProfileId(openId); }
    }
  }, [params, suppliers]); // eslint-disable-line react-hooks/exhaustive-deps

  const addSubcat = useMutation({
    mutationFn: async (label) => (await api.post("/supplier-subcats", { label })).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["supplier-subcats"] }); setAddingSub(false); setNewSubLabel(""); toast.success(`Subcategory "${d.label}" added`); },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not add subcategory"),
  });
  const create = useMutation({
    mutationFn: async () => (await api.post("/suppliers", createDraft)).data,
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["suppliers"] });
      setCreateOpen(false); setCreateDraft({ ...EMPTY });
      toast.success("Supplier created — send their onboarding link when ready");
      setTab(d.primary_category || "materials"); setProfileId(d.id);
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Could not create supplier"),
  });

  const counts = useMemo(() => {
    const c = { materials: 0, contractors_labour: 0, other: 0 };
    suppliers.forEach((s) => { c[s.primary_category || "other"] = (c[s.primary_category || "other"] || 0) + 1; });
    return c;
  }, [suppliers]);

  const rows = useMemo(() => suppliers
    .filter((s) => (s.primary_category || "other") === tab)
    .filter((s) => tab !== "materials" || subcat === "all" || (s.subcategories || []).includes(subcat)),
  [suppliers, tab, subcat]);

  const profileSup = profileId ? suppliers.find((s) => s.id === profileId) : null;
  const closeProfile = () => {
    setProfileId(null);
    if (params.get("open")) { params.delete("open"); setParams(params, { replace: true }); }
  };

  return (
    <div>
      <PageHeader
        title="Suppliers"
        subtitle="Approved supplier register — onboarding, evaluation, approval and document maintenance. Only approved (or temporarily approved) suppliers can be used on purchase orders."
        breadcrumb={["Database", "Suppliers"]}
        actions={
          <Button data-testid="new-supplier-btn" onClick={() => { setCreateDraft({ ...EMPTY, primary_category: tab === "materials" ? "materials" : tab }); setCreateOpen(true); }} className="rounded-sm h-10 bg-cyan-600 hover:bg-cyan-700 text-white">
            <Plus size={16} className="mr-2" /> New supplier
          </Button>
        }
      />

      <div className="px-6 lg:px-10 py-6 max-w-7xl">
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {PRIMARY_TABS.map(([v, l]) => (
            <button key={v} onClick={() => { setTab(v); setSubcat("all"); }} data-testid={`primary-tab-${v}`}
              className={`px-4 py-2 rounded-sm text-xs font-bold border transition-colors ${tab === v ? "bg-slate-900 border-slate-900 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-slate-400"}`}>
              {l} ({counts[v] || 0})
            </button>
          ))}
          <div className="relative ml-auto w-full sm:w-64">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="supplier-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search suppliers…" className="pl-8 h-9 rounded-sm" />
          </div>
          <DefaultViewControl screen="suppliers"
            current={{ tab, subcat }}
            onApply={(f) => { if (f.tab) setTab(f.tab); if (f.subcat) setSubcat(f.subcat); }} />
        </div>

        {tab === "materials" && (
          <div className="flex flex-wrap items-center gap-1.5 mb-4" data-testid="subcat-chips">
            <button onClick={() => setSubcat("all")} data-testid="subcat-all"
              className={`px-3 py-1 rounded-full text-[11px] font-semibold border ${subcat === "all" ? "bg-cyan-600 border-cyan-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-cyan-400"}`}>All</button>
            {subcats.map((sc) => (
              <button key={sc.slug} onClick={() => setSubcat(sc.slug)} data-testid={`subcat-${sc.slug}`}
                className={`px-3 py-1 rounded-full text-[11px] font-semibold border ${subcat === sc.slug ? "bg-cyan-600 border-cyan-600 text-white" : "bg-white border-slate-300 text-slate-600 hover:border-cyan-400"}`}>
                {sc.label}
              </button>
            ))}
            {addingSub ? (
              <span className="flex items-center gap-1.5">
                <Input autoFocus value={newSubLabel} onChange={(e) => setNewSubLabel(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && newSubLabel.trim() && addSubcat.mutate(newSubLabel.trim())}
                  placeholder="e.g. Timber" className="h-7 w-36 rounded-full text-[11px]" data-testid="new-subcat-input" />
                <Button size="sm" className="h-7 rounded-full text-[10px] bg-cyan-600 hover:bg-cyan-700" disabled={!newSubLabel.trim() || addSubcat.isPending} onClick={() => addSubcat.mutate(newSubLabel.trim())} data-testid="save-subcat-btn">Add</Button>
                <Button size="sm" variant="ghost" className="h-7 rounded-full text-[10px]" onClick={() => { setAddingSub(false); setNewSubLabel(""); }}>Cancel</Button>
              </span>
            ) : (
              <button onClick={() => setAddingSub(true)} data-testid="add-subcat-btn"
                className="px-3 py-1 rounded-full text-[11px] font-semibold border border-dashed border-cyan-500 text-cyan-700 hover:bg-cyan-50">+ Add subcategory</button>
            )}
          </div>
        )}

        <div className="border border-slate-200 bg-white industrial-shadow overflow-x-auto">
          <table className="w-full text-sm min-w-[760px]">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                {["Supplier", "Subcategories", "Contact", "Approval", "Documents", ""].map((h) => (
                  <th key={h} className="text-left px-4 py-2.5 text-[9px] font-bold tracking-widest uppercase text-slate-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-sm text-slate-400" data-testid="suppliers-empty">No suppliers here yet.</td></tr>
              )}
              {rows.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 last:border-b-0 hover:bg-cyan-50/30" data-testid={`supplier-row-${s.id}`}>
                  <td className="px-4 py-3">
                    <button onClick={() => setProfileId(s.id)} className="font-display font-semibold text-slate-900 hover:text-cyan-700 text-left" data-testid={`supplier-name-${s.id}`}>{s.name}</button>
                    {s.critical && <div className="mt-1"><CriticalChip /></div>}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {tab === "materials"
                        ? (s.subcategories || []).map((sc) => <span key={sc} className="inline-block bg-slate-100 border border-slate-200 rounded-sm text-[9.5px] font-semibold text-slate-600 px-1.5 py-0.5">{subLabel[sc] || sc}</span>)
                        : <span className="inline-block bg-slate-100 border border-slate-200 rounded-sm text-[9.5px] font-semibold text-slate-600 px-1.5 py-0.5">{tab === "contractors_labour" ? "Contractors / Labour" : "Other"}</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {s.email && <div className="flex items-center gap-1.5 truncate max-w-[200px]"><Mail size={10} className="text-slate-400 shrink-0" />{s.email}</div>}
                    {s.phone && <div className="flex items-center gap-1.5 mt-0.5"><Phone size={10} className="text-slate-400 shrink-0" />{s.phone}</div>}
                  </td>
                  <td className="px-4 py-3"><StatusChip s={s} /></td>
                  <td className="px-4 py-3"><DocsChip s={s} /></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {s.onboarding?.status === "sent" && obLink(s) ? (
                      <Button size="sm" variant="outline" className="rounded-sm h-7 text-[10.5px] mr-1.5" onClick={() => copyText(obLink(s))} data-testid={`copy-link-${s.id}`}>
                        <Link2 size={11} className="mr-1" /> Copy link
                      </Button>
                    ) : s.approval_status === "pending_review" ? (
                      <Button size="sm" className="rounded-sm h-7 text-[10.5px] bg-violet-600 hover:bg-violet-700 mr-1.5" onClick={() => setProfileId(s.id)} data-testid={`review-${s.id}`}>
                        <ShieldCheck size={11} className="mr-1" /> Review
                      </Button>
                    ) : (!s.approval_status || ["not_onboarded", "approved_legacy", "changes_requested"].includes(s.approval_status)) ? (
                      <Button size="sm" className="rounded-sm h-7 text-[10.5px] bg-cyan-600 hover:bg-cyan-700 mr-1.5" onClick={() => { setProfileId(s.id); setSendFor(s.id); }} data-testid={`onboard-${s.id}`}>
                        <Send size={11} className="mr-1" /> Onboard
                      </Button>
                    ) : null}
                    <Button size="sm" variant="outline" className="rounded-sm h-7 text-[10.5px]" onClick={() => setProfileId(s.id)} data-testid={`profile-${s.id}`}>Profile</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-slate-400 mt-3">
          Legacy suppliers were grandfathered as <b>approved</b> and flagged for review — send them an onboarding link to complete a full evaluation.
          Document expiry reminders repeat weekly (staff bell + supplier email) until renewed copies are uploaded.
        </p>
      </div>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-sm sm:max-w-xl max-h-[92vh] overflow-y-auto" data-testid="new-supplier-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">New supplier</DialogTitle></DialogHeader>
          <SupplierForm draft={createDraft} setDraft={setCreateDraft} subcats={subcats} />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCreateOpen(false)} className="rounded-sm">Cancel</Button>
            <Button data-testid="create-supplier-btn" disabled={create.isPending || !createDraft.name?.trim()} onClick={() => create.mutate()} className="rounded-sm bg-cyan-600 hover:bg-cyan-700">
              {create.isPending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Plus size={14} className="mr-1.5" />} Create supplier
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {profileSup && (
        <ProfileDialog sup={profileSup} subcats={subcats} canApprove={canApprove} canBank={canBank}
          onClose={closeProfile} onSendOnboarding={() => setSendFor(profileSup.id)} />
      )}
      <SendOnboardingDialog sup={suppliers.find((s) => s.id === sendFor)} open={!!sendFor} onClose={() => setSendFor(null)} />
    </div>
  );
}
